import { useState } from "react";

import type { LayoutChangeEvent } from "react-native";

import { SMTTestID } from "@/constants/TestID";
import {
  DayEntryPill,
  SMTStreakGradient,
} from "@/ui/components/smt/SMTWeekLogSection/SMTDayEntryComponents";
import { Button, FAIconName, Text } from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import {
  generateWeekData,
  getStreakStateFromStreaks,
  SMTWeekLogStreakDescription,
  SMTWeekLogStreakLabel,
} from "@/utils/smt.util";
import { joinTestIDs } from "@/utils/string.util";

interface SMTWeekLogProps {
  canAddEntries: boolean;
  datesWithLogs: Date[];
  globalStreak: number;
  onAddEntryPress: () => void;
}

enum SMTWeekLogContent {
  AddButtonTitle = "Add",
}

const SMTWeekLog = ({
  canAddEntries,
  datesWithLogs,
  globalStreak,
  onAddEntryPress,
}: SMTWeekLogProps) => {
  const baseTestID = joinTestIDs(SMTTestID.Primary, SMTTestID.WeekLog);

  const [containerWidth, setContainerWidth] = useState<number>(0);

  const { streaks, weekDays } = generateWeekData(datesWithLogs);

  const streakState = getStreakStateFromStreaks({ globalStreak, weekDays });

  const handleLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const description = SMTWeekLogStreakDescription[streakState];
  const label = SMTWeekLogStreakLabel[streakState]({
    streakLength: globalStreak,
  });

  return (
    <BaseCard testID={baseTestID}>
      <VStack gap={18}>
        <Box onLayout={handleLayout} position="relative">
          <HStack
            alignItems="center"
            justifyContent="space-between"
            position="relative"
          >
            {containerWidth > 0 &&
              streaks.map((streak) => (
                <SMTStreakGradient
                  containerWidth={containerWidth}
                  endIndex={streak.endIndex}
                  key={`streak-${streak.startIndex}-${streak.endIndex}`}
                  numberOfDays={weekDays.length}
                  startIndex={streak.startIndex}
                />
              ))}
            {weekDays.map((day) => {
              const key = day.date.toISOString();
              return (
                <Box key={key} zIndex={1}>
                  <DayEntryPill
                    date={day.date}
                    hasEntries={day.hasLogs}
                    isPartOfStreak={day.isPartOfStreak}
                  />
                </Box>
              );
            })}
          </HStack>
        </Box>

        {canAddEntries && (
          <HStack alignItems="center" gap={12} justifyContent="space-between">
            <Text
              testID={joinTestIDs(baseTestID, "streak-text")}
              variant="bodyMedium"
            >
              {label}
            </Text>
            <Button
              leftIcon={FAIconName.Plus}
              onPress={onAddEntryPress}
              size="small"
              testID={joinTestIDs(baseTestID, "add-entry")}
              title={SMTWeekLogContent.AddButtonTitle}
              variant="accent.blue"
            />
          </HStack>
        )}
        {canAddEntries && description && (
          <Text
            testID={joinTestIDs(baseTestID, "streak-text")}
            variant="caption"
          >
            {description}
          </Text>
        )}
      </VStack>
    </BaseCard>
  );
};

export default SMTWeekLog;
export { SMTWeekLogContent };
export type { SMTWeekLogProps };
